import {
  Children,
  PropsWithChildren,
  ReactElement,
  ReactNode,
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';
import { css } from '@emotion/react';
import { theme } from '@frontend/theme';
import { Button } from '@frontend/design-system';

const PADDING = 24;

const getItemCss = (totalItems: number) => {
  switch (totalItems) {
    case 1:
      return null;

    case 2:
      return styles.twoItems;

    default:
      return [styles.twoItems, styles.moreThanTwoItems];
  }
};

interface Props extends PropsWithChildren {
  onScreenCardsCountChangeHandler?: (count: number) => void;
}

export const Carousel = ({ children, onScreenCardsCountChangeHandler, ...rest }: Props) => {
  const visibleChildren = Children.toArray(children).filter((child) => {
    const element = child as React.ReactElement<{
      children?: ReactNode;
      isVisible?: boolean;
    }>;
    return (typeof element?.props?.isVisible === 'undefined' || element?.props?.isVisible) && element?.props?.children;
  });
  const totalItems = visibleChildren.length;

  const [activeIndex, setActiveIndex] = useState<number>(0);
  const [hasLeftScroll, setHasLeftScroll] = useState<boolean>(false);
  const [hasRightScroll, setHasRightScroll] = useState<boolean>(false);
  const [shouldShowButtons, setShouldShowButtons] = useState<boolean>(false);
  const [onScreenCardsCount, setOnScreenCardsCount] = useState<number>(0);
  const carouselRef = useRef<HTMLDivElement>(null);

  const nextClickHandler = useCallback(() => {
    setActiveIndex((prevIndex) => {
      const newIndex = prevIndex + 1;
      return newIndex >= totalItems ? 0 : newIndex;
    });
  }, [totalItems]);

  const prevClickHandler = useCallback(() => {
    setActiveIndex((prevIndex) => {
      const newIndex = prevIndex - 1;
      return newIndex < 0 ? totalItems - 1 : newIndex;
    });
  }, [totalItems]);

  const resetScroll = useCallback(() => {
    // Reset scroll position
    if (carouselRef.current) {
      carouselRef.current.scrollTo({
        left: 0,
      });
      setActiveIndex(0);
      setHasLeftScroll(false);
    }
  }, []);

  useLayoutEffect(() => {
    if (!carouselRef.current) {
      return;
    }

    const resizeHandler = () => {
      if (carouselRef.current) {
        const { offsetWidth, scrollWidth } = carouselRef.current;
        const hasScroll = scrollWidth > offsetWidth;
        setShouldShowButtons(hasScroll);
        resetScroll();
        setHasRightScroll(hasScroll);

        // Get the width of the first child
        // All children should have the same width
        // Using the above width guess the count of on screen cards
        if (totalItems > 1) {
          const firstItem = carouselRef.current.children[0] as HTMLElement;
          const itemWidth = firstItem.offsetWidth;
          setOnScreenCardsCount(Math.floor(offsetWidth / itemWidth));
        } else {
          setOnScreenCardsCount(1);
        }
      }
    };

    const resizeObserver = new ResizeObserver(resizeHandler);
    resizeObserver.observe(carouselRef.current);
    resizeHandler();

    return () => {
      resizeObserver.disconnect();
    };
  }, [carouselRef.current, totalItems]);

  useEffect(() => {
    resetScroll();
  }, [carouselRef.current, shouldShowButtons]);

  useEffect(() => {
    // Scroll to the active item
    if (carouselRef.current) {
      const { offsetWidth, scrollWidth } = carouselRef.current;
      const itemWidth = scrollWidth / totalItems;
      const scrollLeft =
        ((activeIndex * itemWidth) % scrollWidth) + (activeIndex === 0 || offsetWidth === itemWidth ? 0 : PADDING);

      carouselRef.current.scrollTo({
        left: scrollLeft,
        behavior: 'smooth',
      });
      setHasLeftScroll(scrollLeft > 0);
      setHasRightScroll(scrollLeft < scrollWidth - offsetWidth);
    }
  }, [activeIndex, carouselRef.current]);

  useEffect(() => {
    onScreenCardsCountChangeHandler?.(onScreenCardsCount);
  }, [onScreenCardsCount]);

  return (
    <div css={styles.wrapper} {...rest}>
      {shouldShowButtons && (
        <Button disabled={!hasLeftScroll} iconName='caret-left' onClick={prevClickHandler} variant='secondary' />
      )}
      <div css={styles.carousel} ref={carouselRef}>
        {Children.map(children, (child) => (
          <div
            css={[styles.item, getItemCss(totalItems)]}
            //  TODO :: add support to disable the rendering of invisble item using a prop
            //  Currently adding the support for display based visibility so we can still render the item to process it's internal functioning as required for the ROI cards
            style={{ display: (child as ReactElement).props.isVisible ? 'block' : 'none' }}
          >
            {child}
          </div>
        ))}
      </div>
      {shouldShowButtons && (
        <Button disabled={!hasRightScroll} iconName='caret-right' onClick={nextClickHandler} variant='secondary' />
      )}
    </div>
  );
};

Carousel.Item = Object.assign(
  ({ children }: PropsWithChildren<{ isVisible?: boolean }>) => {
    return children;
  },
  { displayName: 'Carousel.Item' }
);

const styles = {
  wrapper: css`
    align-items: center;
    display: flex;
    column-gap: ${theme.spacing(1)};
    min-width: 364px;
  `,

  carousel: css`
    display: flex;
    flex-wrap: nowrap;
    flex: 1;
    overflow: hidden;

    @container (min-width: 740px) {
      gap: ${PADDING}px;
    }
  `,

  item: css`
    min-width: 100%;
    max-width: 100%;
  `,

  twoItems: css`
    @container (min-width: 740px) {
      min-width: calc(100% / 2 - ${theme.spacing(1.5)});
      max-width: calc(100% / 2 - ${theme.spacing(1.5)});
    }
  `,

  moreThanTwoItems: css`
    @container (min-width: 1120px) {
      min-width: calc(100% / 3 - ${theme.spacing(2)});
      max-width: calc(100% / 3 - ${theme.spacing(2)});
    }
  `,
};
